import {
  Grid, Typography, Paper, TableRow, TableCell, Box,
  makeStyles, Collapse, Theme, TableContainer, Table,
  TableHead, TableBody, IconButton, Chip
} from "@material-ui/core";
import { AddBoxOutlined, IndeterminateCheckBoxOutlined } from '@material-ui/icons';
import { useState, useMemo, useEffect } from 'react';
import { useRocInternalPortalStore } from 'apps/roc-internal-portal/src/app/hooks';
import { AIDocType, PostClosingReviewGroup } from '@roc/feature-documents';
import { ReviewTableItemValue } from "./ReviewTableItem";
import { DocReviewBaseStore } from "../store/docReviewBaseStore";
import { observer } from "mobx-react";
import { StatusChip, StatusDiv, StatusType } from '@roc/ui';

const processingStatusText: Record<string, string> = {
  IN_PROGRESS: 'Processing uploaded file',
  SUCCESS: 'Document ready',
  FAILED: 'Document extracted with low accuracy',
};

const useStyles = makeStyles((theme: Theme) => ({
  titleRow: {
    height: '1.8em',
    borderLeft: '1px solid',
    borderLeftColor: '#e6f6fe',
    borderRight: '1px solid',
    borderRightColor: '#e6f6fe'
  },
  statusChipItem: {
    marginLeft: theme.spacing(1),
  },
  headerCell: {
    fontWeight: 'bold',
    padding: 0,
    textAlign: 'left',
  },
  headerCellCenter: {
    textAlign: 'center'
  },
  tableContainerWrapper: {
    padding: '8px',
    backgroundColor: '#F0F0F0',
    width: '100%',
    boxSizing: 'border-box',
  },
  tableContainer: {
    border: '1px #e0e0e0 solid',
    backgroundColor: '#FFF',
    width: '100%',
    boxSizing: 'border-box',
  },
  collapsableSection: {
    padding: '25px'

  },
  subSectionTitle: {
    fontSize: '20px',
  },
  table: {
    margin: 0,
    padding: 0,
    '& td': {
      padding: '5px 0',
    },
    '& th:first-child, td[rowSpan], $addNewItemRow td, td:first-child': {
      paddingLeft: theme.spacing(2),
      paddingRight: '3px',
    },
    '& table tr:last-child td, & table td[rowSpan]': {
      borderBottom: 'none',
    },
    '& input': {
      padding: '12px',
      fontSize: '0.875rem'
    }
  },
  section: {
    padding: '20px 20px 0 20px'

  },
  sectionTitle: {
    fontSize: '20px',
  },
  sectionTitleContainer: { padding: '0 15px 15px 15px', borderBottom: '1.5px solid #E0E0E0FF' },
  paper: { border: '1.5px solid #E0E0E0FF' },
  expandMainSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%'
  },
  tableHeader: { padding: '15px', borderBottom: '1.5px solid #E0E0E0FF', display: 'flex', alignItems: 'center' },
  itemDisplayNameCell: {
    width: '15%',
    textAlign: 'left',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  dataCell: {
    width: '20%',
    textAlign: 'left',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  needsRevisionFlagCell: {
    width: '10%',
    textAlign: 'center',
  },
  sendForRevisionCell: {
    width: '10%',
    textAlign: 'center',
  },
  cell: {
    width: '20%',
    textAlign: 'center',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  noBorderCell: {
    border: 0,
  },
  reviewSectionContainer: {
    paddingBottom: '20px'
  },
  chipError: {
    color: theme.palette.error.dark,
    borderRadius: '8px',
    borderColor: theme.palette.error.dark,
    width: '55px',
    margin: '0 15px'
  },
  groupPaper: {
    border: '1.5px solid #CFD9E4',
    backgroundColor: '#F7F9FB',
    width: '100%',
  },
  groupHeader: {
    padding: '10px 15px',
    display: 'flex',
    alignItems: 'center',
  },
  groupTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    letterSpacing: '0.04em',
  },
  groupDocumentCount: {
    fontSize: '14px',
    color: '#57564F',
    marginLeft: theme.spacing(2),
  },
  groupBody: {
    padding: '0 15px 15px 15px',
  },
  groupSectionItem: {
    paddingTop: '10px',
  },
  groupEmpty: {
    padding: '0 15px 15px 15px',
    fontSize: '14px',
    color: '#6B6B64',
  },
  groupContainer: {
    padding: '20px 20px 0 20px',
  },
  chipSuccess: {
    color: theme.palette.success.dark,
    backgroundColor: '#FFF',
    borderRadius: '8px',
    borderColor: theme.palette.success.dark,
    width: '55px',
    margin: '0 15px'
  },
}));

export const columns = [
  { name: 'Fields', mapping: 'itemDisplayName' },
  { name: 'Document Data', mapping: 'data' },
  { name: 'Portal Value', mapping: 'expectedData' },
  { name: 'Resolution?', mapping: 'sendForRevision', className: 'headerCellCenter' },
  { name: 'Reviewer Comment', mapping: 'reviewerComment', className: 'headerCellCenter' }
];

const columnsFor = (docType: AIDocType) =>
  docType === AIDocType.POST_CLOSING_REVIEW
    ? columns.filter(column => column.mapping !== 'data')
    : columns;

const OTHER_GROUP = 'Other';

const isSectionVisible = (section, store: DocReviewBaseStore) =>
  section &&
  (store.showAllOnRevision ||
    section.status === 'IN_PROGRESS' ||
    section.status === 'FAILED' ||
    !section.items?.length ||
    section.items?.some(item => item.sendForRevision));

const getGroupName = (section): string => section?.type || OTHER_GROUP;

export const AIDocReviewTable = observer(
  ({
    docType,
    store,
    loanId,
  }: {
    docType: AIDocType;
    store: DocReviewBaseStore;
    loanId: any;
  }) => {
    const classes = useStyles();
    const isGrouped = docType === AIDocType.POST_CLOSING_REVIEW;

    const visibleSections = store.docReviewData.filter(section =>
      isSectionVisible(section, store)
    );

    const groupNames = useMemo(() => {
      const names: string[] = Object.values(PostClosingReviewGroup);
      store.docReviewData.forEach(section => {
        const name = getGroupName(section);
        if (!names.includes(name)) {
          names.push(name);
        }
      });
      return names;
    }, [store.docReviewData]);

    return (
      <Grid container className={classes.reviewSectionContainer}>
        <Grid
          container
          item
          xs={12}
          style={{ maxHeight: '55vh', overflow: 'auto' }}
        >
          {isGrouped ? (
            groupNames.map(groupName => (
              <Grid
                item
                xs={12}
                className={classes.groupContainer}
                key={groupName}
              >
                <ReviewTableGroup
                  docType={docType}
                  groupName={groupName}
                  sections={store.docReviewData.filter(
                    section => getGroupName(section) === groupName
                  )}
                  visibleSections={visibleSections.filter(
                    section => getGroupName(section) === groupName
                  )}
                  store={store}
                  loanId={loanId}
                />
              </Grid>
            ))
          ) : !store.reviewDataAvailable ? (
            <TableRow>
              <TableCell colSpan={10} className={classes.noBorderCell}>
                <Box>No information has been added yet</Box>
              </TableCell>
            </TableRow>
          ) : (
            visibleSections.map(section => (
              <Grid item xs={12} className={classes.section} key={section.sectionName}>
                <ReviewTableSection
                  docType={docType}
                  section={section}
                  store={store}
                  loanId={loanId}
                />
              </Grid>
            ))
          )}
        </Grid>
      </Grid>
    );
  }
);

const ReviewTableGroup = observer(
  ({
    docType,
    groupName,
    sections,
    visibleSections,
    store,
    loanId,
  }: {
    docType: AIDocType;
    groupName: string;
    sections: DocReviewSection[];
    visibleSections: DocReviewSection[];
    store: DocReviewBaseStore;
    loanId: any;
  }) => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);

    const groupItems = sections.reduce(
      (items, section) => items.concat(section.items ?? []),
      []
    );
    const flaggedItemCount = groupItems.filter(item => item.sendForRevision)
      .length;

    return (
      <Paper className={classes.groupPaper}>
        <Grid item xs={12} container className={classes.groupHeader} direction="row">
          <Grid item>
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? (
                <IndeterminateCheckBoxOutlined />
              ) : (
                <AddBoxOutlined />
              )}
            </IconButton>
          </Grid>
          <Grid item>
            <Typography className={classes.groupTitle}>{groupName}</Typography>
          </Grid>
          <Grid item>
            <Typography className={classes.groupDocumentCount}>
              {`${sections.length} ${
                sections.length === 1 ? 'document' : 'documents'
              }`}
            </Typography>
          </Grid>
          {groupItems.length > 0 && (
            <Grid item>
              <Chip
                variant="outlined"
                className={
                  flaggedItemCount === 0 ? classes.chipSuccess : classes.chipError
                }
                label={`${flaggedItemCount}/${groupItems.length}`}
              />
            </Grid>
          )}
        </Grid>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {visibleSections.length === 0 ? (
            <Box className={classes.groupEmpty}>
              {sections.length === 0
                ? 'No documents'
                : 'No documents flagged for revision'}
            </Box>
          ) : (
            <Grid container className={classes.groupBody}>
              {visibleSections.map(section => (
                <Grid
                  item
                  xs={12}
                  className={classes.groupSectionItem}
                  key={section.sectionName}
                >
                  <ReviewTableSection
                    docType={docType}
                    section={section}
                    store={store}
                    loanId={loanId}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Collapse>
      </Paper>
    );
  }
);


type DocReviewSection = {
  type?: string;
  sectionName: string;
  items: any[];
  status?: 'IN_PROGRESS' | 'SUCCESS' | 'FAILED';
  errorMessage?: string;
};

const ReviewTableSection = observer(
  ({
    docType,
    section,
    store,
    loanId,
  }: {
    docType: AIDocType;
    section: DocReviewSection;
    store: DocReviewBaseStore;
    loanId: any;
  }) => {
    const title = section.sectionName;
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);
    const { docReviewStore } = useRocInternalPortalStore();
    const sectionColumns = columnsFor(docType);

    const sectionItems = useMemo(() => store.getSectionItems(title), [
      store.docReviewData,
      store.showAllOnRevision,
    ]);
    const flaggedItemsBySectionCount = useMemo(
      () => store.getFlaggedItemCount(title),
      [store.docReviewData, store.showAllOnRevision]
    );

    const getClassNameByColumn = mapping => {
      switch (mapping) {
        case 'itemDisplayName':
          return classes.itemDisplayNameCell;
        case 'data':
          return classes.dataCell;
        case 'needsRevisionFlag':
          return classes.needsRevisionFlagCell;
        case 'sendForRevision':
          return classes.sendForRevisionCell;
        default:
          return classes.cell;
      }
    };

    return (
      <Paper className={classes.paper}>
        <Grid
          item
          xs={12}
          container
          className={classes.tableHeader}
          direction="row"
        >
          <Grid item>
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? (
                <IndeterminateCheckBoxOutlined />
              ) : (
                <AddBoxOutlined />
              )}
            </IconButton>
          </Grid>
          <Grid item>
            <Typography className={classes.sectionTitle}>{title}</Typography>
          </Grid>
          {sectionItems.length > 0 && (
            <Grid item>
              <Chip
                variant="outlined"
                className={
                  flaggedItemsBySectionCount === 0
                    ? classes.chipSuccess
                    : classes.chipError
                }
                label={`${flaggedItemsBySectionCount}/${sectionItems.length}`}
              />
            </Grid>
          )}
          {section.status && (
            docType === AIDocType.CLOSING_DOCS ? (
              <Grid item className={classes.statusChipItem}>
                <StatusChip
                  statusType={StatusType.DOC_AUTOMATION}
                  label={section.status}
                />
              </Grid>
            ) : (
              <Grid item xs={12} className={classes.statusChipItem}>
                <StatusDiv
                  status={section.status}
                  text={
                    section.status === 'FAILED' && section.errorMessage
                      ? section.errorMessage
                      : processingStatusText[section.status] ?? ''
                  }
                  onRefresh={() => store.getDocReviewData(loanId, docType)}
                />
              </Grid>
            )
          )}
        </Grid>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {!sectionItems?.length ? (
            <Box className={classes.groupEmpty}>No data has been extracted from this document yet</Box>
          ) : (
          <Grid item xs={12} className={classes.expandMainSection}>
            <Grid container className={classes.tableContainerWrapper} xs={12}>
              <TableContainer className={classes.tableContainer}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow className={classes.titleRow}>
                      {sectionColumns.map(column => {
                        return (
                          <TableCell
                            className={`${classes.headerCell} ${
                              classes[column.className] ?? ''
                            }`}
                          >
                            {' '}
                            {column.name}{' '}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sectionItems
                      .filter(
                        item =>
                          docReviewStore.showAllOnRevision ||
                          item.sendForRevision
                      )
                      .map((item, index) => (
                        <TableRow key={item.itemDisplayName ?? index}>
                          {sectionColumns.map(column => {
                            item = { ...item, sectionName: title };
                            return (
                              <TableCell
                                className={getClassNameByColumn(column.mapping)}
                                align="center"
                              >
                                <ReviewTableItemValue
                                  docType={docType}
                                  item={item}
                                  column={column}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          )}
        </Collapse>
      </Paper>
    );
  }
);